import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND } from "../data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/employers", label: "Employers" },
  { to: "/job-seekers", label: "Job Seekers" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-3 sm:top-4 left-0 right-0 z-50 px-3 sm:px-4"
      data-testid="navbar"
    >
      <div className={`max-w-6xl mx-auto flex items-center justify-between rounded-2xl border transition-all duration-500 px-4 sm:px-6 ${scrolled ? "bg-[#0B0F19]/85 backdrop-blur-xl border-slate-800/80 py-2.5 shadow-lg shadow-black/30" : "bg-[#0B0F19]/40 backdrop-blur-md border-slate-800/40 py-3.5"}`}>
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-accent font-bold text-lg">H</span>
          <span className="font-display font-bold text-lg tracking-tight text-white">{BRAND.name}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={`relative px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-300 ${pathname === n.to ? "text-amber-400" : "text-slate-300 hover:text-white"}`}
            >
              {n.label}
              {pathname === n.to && (
                <motion.span layoutId="nav-underline" className="absolute left-3 right-3 -bottom-0.5 h-px bg-amber-400" />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link to="/job-seekers" data-testid="nav-find-jobs" className="px-3.5 py-2 text-sm font-semibold text-slate-200 border border-slate-700/80 rounded-lg hover:border-slate-500 hover:text-white transition-all duration-300">Find Jobs</Link>
          <Link to="/employers" data-testid="nav-request-staff" className="group px-4 py-2 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-lg shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 transition-all duration-300 flex items-center gap-1">
            Request Staff <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <button data-testid="mobile-menu-toggle" className="lg:hidden text-white p-1" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden mt-2 max-w-6xl mx-auto rounded-2xl border border-slate-800/80 bg-[#0B0F19]/95 backdrop-blur-xl p-4"
            data-testid="mobile-menu"
          >
            <div className="grid grid-cols-2 gap-1">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${pathname === n.to ? "text-amber-400 bg-amber-500/10" : "text-slate-300"}`}>{n.label}</Link>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Link to="/job-seekers" className="text-center px-3 py-2.5 text-sm font-semibold text-slate-200 border border-slate-700 rounded-lg">Find Jobs</Link>
              <Link to="/employers" className="text-center px-3 py-2.5 text-sm font-semibold text-slate-950 bg-amber-400 rounded-lg">Request Staff</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
